// remote-evidence-candidate-binding.ts
//
// X2 (design H14 bản 2 B2-4, 2026-09-29): remote review evidence opens G3 only
// when it is evidence about THE commit being released. The old check
// (checkVerdictStillBoundToCurrentBase) compared the base SHA only — measured
// with a fake GitLab: the MR reported head `4c1475e5…` while the pushed
// candidate was `c5ec7c73…`, and G3 was requested anyway, i.e. CI results of
// another commit could open the release gate. Now all three must hold:
//   - MR head SHA  == the commit the draft-MR push put on the branch
//                     (`pushedSha` — the candidate minus controller-owned
//                     paths, NOT the mission HEAD);
//   - MR base SHA  == the mission's base;
//   - pipeline SHA == pushedSha. A host that reports no pipeline is recorded
//     as `pipeline_sha: not_reported` and does NOT pass — no CI evidence is
//     not evidence (fail closed; run CI on the MR, then `run` again).
import { clipUntrustedText } from "./clip-untrusted-text.js";
import { PIPELINE_APPEARANCE_GRACE_SEC } from "./release-ci-gate.js";
import {
  checkVerdictStillBoundToCurrentBase,
  type RemoteEvidence,
} from "./review-remote-adapter.js";

export interface RemoteEvidenceBindingCheck {
  ok: boolean;
  reason: string;
  /**
   * Set when what is missing is the pipeline OF THE PUSHED COMMIT (none at all,
   * or only another commit's) — the one mismatch that can clear by itself while
   * GitLab creates the pipeline (round 2 L6). A head/base mismatch never is.
   */
  pipelineMissingForCommit?: boolean;
  /**
   * Set when the MR reports no base SHA at all: GitLab fills `diff_refs` a few
   * seconds after it creates the MR (dogfood Beta 12, F-B12-1), so right after
   * the push this clears by itself like a pipeline that has not appeared yet.
   * A base that is present and different is never this, and neither is an
   * empty base where no base is expected (the approved-G3 leg passes the
   * evidence's own base: it does not re-check the base at all).
   */
  mergeRequestDiffNotReady?: boolean;
}

/**
 * `stop` is what a mismatch holds back, named in the reason: G3 before it is
 * asked (default), the deploy when the CI gate re-checks an approved G3
 * (release-ci-gate-approved-candidate.ts).
 */
export function checkRemoteEvidenceBoundToPushedCandidate(
  evidence: Pick<RemoteEvidence, "headSha" | "baseSha" | "ciStatus" | "pipelineSha">,
  expected: { pushedSha: string; baseSha: string },
  stop = "không xin G3",
): RemoteEvidenceBindingCheck {
  // SHAs below come from the server: shown without control characters, bounded.
  const shown = (sha: string) => clipUntrustedText(sha, 64);
  if (evidence.headSha !== expected.pushedSha) {
    return {
      ok: false,
      reason:
        `Head SHA của MR (${shown(evidence.headSha) || "(trống)"}) khác commit vừa push ` +
        `(${expected.pushedSha}) ⇒ bằng chứng remote thuộc commit khác, ${stop}.`,
    };
  }
  if (evidence.baseSha === "" && expected.baseSha !== "") {
    return {
      ok: false,
      mergeRequestDiffNotReady: true,
      reason:
        "Base SHA của MR trống — GitLab chưa trả `diff_refs` (thường có sau vài giây kể từ khi MR " +
        `được tạo) ⇒ chưa đối chiếu được base, ${stop}. Chạy lại \`avatar autopilot run\`; trống quá ` +
        `${PIPELINE_APPEARANCE_GRACE_SEC}s sau push thì \`blocked:release_flow\` — kiểm MR trên GitLab.`,
    };
  }
  const base = checkVerdictStillBoundToCurrentBase(
    { headSha: evidence.headSha, baseSha: evidence.baseSha, risk: "", ciStatus: evidence.ciStatus },
    expected.baseSha,
  );
  if (!base.ok) return base;
  if (evidence.pipelineSha === undefined) {
    return {
      ok: false,
      pipelineMissingForCommit: true,
      reason:
        "pipeline_sha: not_reported — MR chưa có pipeline nào ⇒ không có bằng chứng CI cho " +
        `commit ${expected.pushedSha}, ${stop}. Chạy CI trên MR rồi chạy lại \`run\`.`,
    };
  }
  if (evidence.pipelineSha !== expected.pushedSha) {
    return {
      ok: false,
      pipelineMissingForCommit: true,
      reason:
        `Pipeline mới nhất của MR chạy trên ${shown(evidence.pipelineSha)}, không phải commit vừa push ` +
        `(${expected.pushedSha}) ⇒ kết quả CI không thuộc candidate, ${stop}.`,
    };
  }
  return { ok: true, reason: "Head, base và pipeline SHA khớp candidate đã push." };
}
